// Helper function to convert kebab-case to camelCase
export const toCamelCase = (str: string): string => {
  return str.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
};

// Helper function to get CSS module class safely
export const getModuleClass = (
  styles: Record<string, string>,
  className: string
): string => {
  const camelCaseClass = toCamelCase(className);
  return styles[camelCaseClass] || "";
};

// Helper function to build class names with CSS modules support
export const buildClassNames = (
  styles: Record<string, string>,
  baseClass: string,
  additionalClasses: (string | false | null | undefined)[]
): string => {
  const classes = [
    styles[baseClass] || baseClass,
    ...additionalClasses.map((cls) => cls && getModuleClass(styles, cls)),
  ];

  return classes.filter(Boolean).join(" ");
};

// Helper function for development warnings about missing CSS classes
export const warnMissingClass = (
  styles: Record<string, string>,
  className: string,
  type: string = "class"
): void => {
  if (import.meta.env.MODE === "development") {
    const camelCaseClass = toCamelCase(className);
    if (!(camelCaseClass in styles)) {
      console.warn(
        `Missing CSS module ${type} for "${className}" (looking for "${camelCaseClass}")`
      );
    }
  }
};

/**
 * Generates a base64-like hash string from input data
 * Useful for creating stable keys in React lists
 *
 * @param input - The string or object to create a hash from
 * @param length - Length of the resulting hash (default: 5)
 * @returns A string hash in base64-like format
 */
export function generateHashKey(
  input: string | number | object,
  length = 5
): string {
  // Convert input to string if it's not already
  const stringInput = typeof input === "string" ? input : JSON.stringify(input);

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < stringInput.length; i++) {
    const char = stringInput.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  // Convert to base64-like string and take specified length
  const hashStr = btoa(
    String.fromCharCode(...new Uint8Array(new Int32Array([hash]).buffer))
  )
    .replace(/[+/=]/g, "")
    .substring(0, length);

  return `key_${hashStr}`;
}

/**
 * Creates a unique key generator for a specific component or list
 * Returns a function that produces stable keys based on item content and position
 *
 * @param prefix - Prefix to make keys more specific to a component
 * @returns A function that generates keys
 */
export function createKeyGenerator<T = unknown>(prefix: string) {
  return (item: T, index: number): string => {
    // Combine the item data with its index and prefix for uniqueness
    const input = `${prefix}_${index}_${JSON.stringify(item)}`;
    return generateHashKey(input);
  };
}
